import apiClient from '@/lib/api/apiClient'
import { extractErrorMessage } from '@/lib/utils/errorHandling'
import type {
  PolicyDeclarationItem,
  PolicyDeclarationListParams,
  PolicyDeclarationListResponse,
  PolicyDeclarationSubmitPayload,
} from '../types/policyDeclaration'

type RawListResponse =
  | PolicyDeclarationItem[]
  | {
      data?: PolicyDeclarationItem[]
      current?: number
      last?: number
      page?: number
      total_pages?: number
    }

function decodeListResponse(
  payload: RawListResponse,
  fallbackPage: number,
): PolicyDeclarationListResponse {
  if (Array.isArray(payload)) {
    return { items: payload, current: fallbackPage, last: 1 }
  }

  const items = Array.isArray(payload?.data) ? payload.data : []
  const current = Number(payload?.current ?? payload?.page ?? fallbackPage) || fallbackPage
  const last = Number(payload?.last ?? payload?.total_pages ?? 1) || 1
  return { items, current, last }
}

export async function getPolicyDeclarations(
  params: PolicyDeclarationListParams,
): Promise<PolicyDeclarationListResponse> {
  try {
    const response = await apiClient.get<RawListResponse>('/api/policy_declaration', { params })
    return decodeListResponse(response.data, params.page ?? 1)
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to load terms & conditions')
  }
}

export async function savePolicyDeclaration(
  payload: PolicyDeclarationSubmitPayload,
  id?: number,
): Promise<void> {
  try {
    const url = id ? `/api/policy_declaration/${id}` : '/api/policy_declaration'
    await apiClient.post(url, payload)
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to save terms & conditions')
  }
}

export async function deletePolicyDeclaration(id: number): Promise<void> {
  try {
    await apiClient.delete(`/api/policy_declaration/${id}`)
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to delete terms & conditions')
  }
}

export async function getPolicyDeclarationById(id: number): Promise<PolicyDeclarationItem> {
  try {
    const response = await apiClient.get<PolicyDeclarationItem>(`/api/policy_declaration/${id}`)
    return response.data
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to load terms & conditions detail')
  }
}

/** Partial POST for drafts / discard (Angular `updateDraft` / `discardDraft`). */
export async function patchPolicyDeclaration(
  id: number,
  payload: Record<string, string | null | undefined>,
): Promise<void> {
  try {
    await apiClient.post(`/api/policy_declaration/${id}`, payload)
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to update terms & conditions')
  }
}
